import { useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { UserRole } from '@myapp/contract'
import { useAuthStore } from '../../features/auth'
import { useUserDetail, useUpdateUser } from '../../entities/user'
import { UserFormWidget, UserFormValues, prepareTechnicianProfileForApi } from '../../features/user-management'
import { routes } from '../../shared/config/routes'
import PageLayout from '../../shared/ui/layouts/PageLayout/PageLayout'
import Loader from '../../shared/ui/feedback/Loader/Loader'

function isoToTime(iso: string): string {
  if (iso.includes('T')) {
    const d = new Date(iso)
    return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
  }
  return iso
}

export function UserEditPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const actor = useAuthStore((s) => s.user)
  const { data: userData, isLoading: isLoadingUser } = useUserDetail(userId ?? '')
  const updateUser = useUpdateUser()
  const submitRef = useRef<(() => void) | null>(null)

  const defaultValues = useMemo(() => {
    if (!userData) return undefined
    const tp = userData.technicianProfile
    return {
      email: userData.email,
      name: userData.name,
      roles: userData.roles as [UserRole, ...UserRole[]],
      technicianProfile: tp
        ? {
            workingDays: tp.workingDays,
            shiftStart: isoToTime(tp.shiftStart),
            shiftEnd: isoToTime(tp.shiftEnd),
            breakStart: isoToTime(tp.breakStart),
            trades: tp.trades,
          }
        : undefined,
    }
  }, [userData])

  if (!userId || !actor?.roles?.length) return null

  if (isLoadingUser) {
    return (
      <PageLayout pageTitle="Benutzer bearbeiten">
        <div className="my-10 flex w-full justify-center">
          <Loader />
        </div>
      </PageLayout>
    )
  }

  const handleSubmit = async (data: UserFormValues) => {
    const patch: Record<string, any> = {}
    if (data.name && data.name !== userData?.name) patch.name = data.name
    if (data.email && data.email !== userData?.email) patch.email = data.email
    const rolesChanged =
      data.roles.length !== (userData?.roles?.length ?? 0) ||
      data.roles.some((r) => !userData?.roles?.includes(r))
    if (rolesChanged) patch.roles = data.roles
    if (data.roles.includes('WORKER') && data.technicianProfile) {
      const prepared = prepareTechnicianProfileForApi(data)
      patch.technicianProfile = (prepared as any).technicianProfile
    }

    if (Object.keys(patch).length > 0) {
      await updateUser.mutateAsync({ id: userId, data: patch })
    }
    navigate(routes.users.root)
  }

  return (
    <PageLayout
      pageTitle="Benutzer bearbeiten"
      scrollable
      back
      backTo={routes.users.root}
      primaryAction={{
        label: 'Speichern',
        onClick: () => submitRef.current?.(),
        loading: updateUser.isPending,
      }}
    >
      <div className="flex flex-row gap-[60px] mt-6">
        <UserFormWidget
          mode="edit"
          actorRoles={actor.roles}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          isLoading={updateUser.isPending}
          submitRef={submitRef}
        />
      </div>
    </PageLayout>
  )
}
